import { getApiBaseUrl } from '../utils/backendBaseUrl';
import { normalizeJoinCode } from './normalizeJoinCode';

export type LiveSessionStatus = 'lobby' | 'question' | 'results' | 'ended' | string;

export type ValidateLiveSessionOk = {
  ok: true;
  pin: string;
  sessionId: string;
  status: LiveSessionStatus;
  expiresAt: number | null;
  sessionMode?: unknown;
  quizRef?: unknown;
};

export type ValidateLiveSessionErrCode =
  | 'INVALID_PIN'
  | 'NOT_FOUND'
  | 'EXPIRED'
  | 'NOT_STARTED'
  | 'FIREBASE_UNAVAILABLE'
  | 'UNAVAILABLE'
  | 'UNKNOWN';

export type ValidateLiveSessionErr = {
  ok: false;
  code: ValidateLiveSessionErrCode;
  message: string;
  httpStatus?: number;
};

function safeMessageForCode(code: ValidateLiveSessionErrCode): string {
  switch (code) {
    case 'INVALID_PIN':
      return 'Codice non valido. Deve essere di 6 caratteri (A-Z, 0-9).';
    case 'NOT_FOUND':
      return 'Sessione non trovata. Controlla il codice e riprova.';
    case 'EXPIRED':
      return 'Sessione scaduta. Chiedi al docente un nuovo codice.';
    case 'NOT_STARTED':
      return 'Sessione non ancora avviata. Attendi che il docente inizi.';
    case 'FIREBASE_UNAVAILABLE':
    case 'UNAVAILABLE':
      return 'Servizio momentaneamente non disponibile. Riprova tra poco.';
    default:
      return 'Impossibile validare la sessione. Riprova.';
  }
}

async function fetchJsonWithTimeout(url: string, timeoutMs: number): Promise<{ status: number; json: any } | null> {
  const controller = new AbortController();
  const t = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { method: 'GET', signal: controller.signal, headers: { Accept: 'application/json' } });
    const ct = String(res.headers.get('content-type') || '');
    const json = ct.includes('application/json') ? await res.json().catch(() => null) : await res.text().catch(() => null);
    return { status: res.status, json };
  } finally {
    window.clearTimeout(t);
  }
}

export async function validateLiveSessionByPin(pinInput: string, opts?: { requireStarted?: boolean; timeoutMs?: number }): Promise<ValidateLiveSessionOk | ValidateLiveSessionErr> {
  const pin = normalizeJoinCode(pinInput).slice(0, 6);
  if (!/^[A-Z0-9]{6}$/.test(pin)) {
    return { ok: false, code: 'INVALID_PIN', message: safeMessageForCode('INVALID_PIN') };
  }

  const apiBase = getApiBaseUrl();
  const requireStarted = Boolean(opts?.requireStarted);
  const url = `${apiBase}/quiz-live/resolve/${encodeURIComponent(pin)}?require_started=${requireStarted ? 'true' : 'false'}`;

  try {
    const out = await fetchJsonWithTimeout(url, opts?.timeoutMs ?? 8000);
    if (!out) {
      return { ok: false, code: 'UNAVAILABLE', message: safeMessageForCode('UNAVAILABLE') };
    }

    if (out.status >= 200 && out.status < 300 && out.json && out.json.ok) {
      return {
        ok: true,
        pin: String(out.json.pin || pin),
        sessionId: String(out.json.sessionId || ''),
        status: String(out.json.status || 'lobby'),
        expiresAt: typeof out.json.expiresAt === 'number' ? out.json.expiresAt : null,
        sessionMode: out.json.sessionMode,
        quizRef: out.json.quizRef
      };
    }

    const detail = out.json?.detail;
    const code = String(detail?.code || '').toUpperCase() as ValidateLiveSessionErrCode;
    const mapped: ValidateLiveSessionErrCode =
      code === 'INVALID_PIN' ||
      code === 'NOT_FOUND' ||
      code === 'EXPIRED' ||
      code === 'NOT_STARTED' ||
      code === 'FIREBASE_UNAVAILABLE'
        ? code
        : out.status === 410
          ? 'EXPIRED'
          : out.status === 404
            ? 'NOT_FOUND'
            : out.status === 400
              ? 'INVALID_PIN'
              : out.status === 409
                ? 'NOT_STARTED'
                : out.status === 503
                  ? 'UNAVAILABLE'
                  : 'UNKNOWN';

    const message = String(detail?.message || '').trim() || safeMessageForCode(mapped);
    return { ok: false, code: mapped, message, httpStatus: out.status };
  } catch {
    return { ok: false, code: 'UNAVAILABLE', message: safeMessageForCode('UNAVAILABLE') };
  }
}

export async function validateLiveSessionBySessionId(sessionId: string, opts?: { requireStarted?: boolean; timeoutMs?: number }): Promise<ValidateLiveSessionOk | ValidateLiveSessionErr> {
  const sid = String(sessionId || '').trim();
  if (!sid) {
    return { ok: false, code: 'NOT_FOUND', message: safeMessageForCode('NOT_FOUND') };
  }

  const apiBase = getApiBaseUrl();
  const requireStarted = Boolean(opts?.requireStarted);
  const url = `${apiBase}/quiz-live/session/${encodeURIComponent(sid)}?require_started=${requireStarted ? 'true' : 'false'}`;

  try {
    const out = await fetchJsonWithTimeout(url, opts?.timeoutMs ?? 8000);
    if (!out) {
      return { ok: false, code: 'UNAVAILABLE', message: safeMessageForCode('UNAVAILABLE') };
    }

    if (out.status >= 200 && out.status < 300 && out.json && out.json.ok) {
      return {
        ok: true,
        pin: String(out.json.pin || ''),
        sessionId: String(out.json.sessionId || sid),
        status: String(out.json.status || 'lobby'),
        expiresAt: typeof out.json.expiresAt === 'number' ? out.json.expiresAt : null,
        sessionMode: out.json.sessionMode,
        quizRef: out.json.quizRef
      };
    }

    const detail = out.json?.detail;
    const code = String(detail?.code || '').toUpperCase() as ValidateLiveSessionErrCode;
    const mapped: ValidateLiveSessionErrCode =
      code === 'INVALID_PIN' ||
      code === 'NOT_FOUND' ||
      code === 'EXPIRED' ||
      code === 'NOT_STARTED' ||
      code === 'FIREBASE_UNAVAILABLE'
        ? code
        : out.status === 410
          ? 'EXPIRED'
          : out.status === 404
            ? 'NOT_FOUND'
            : out.status === 409
              ? 'NOT_STARTED'
              : out.status === 503
                ? 'UNAVAILABLE'
                : 'UNKNOWN';

    const message = String(detail?.message || '').trim() || safeMessageForCode(mapped);
    return { ok: false, code: mapped, message, httpStatus: out.status };
  } catch {
    return { ok: false, code: 'UNAVAILABLE', message: safeMessageForCode('UNAVAILABLE') };
  }
}
